import { CaretRight, User, Calendar, Phone, AirplaneTilt, Archive, Trash, Buildings } from '@phosphor-icons/react';
import { Button } from './ui/button';
import LevelBadge from './LevelBadge';
import AnimatedNumber from './AnimatedNumber';
import AnimatedProgressBar from './AnimatedProgressBar';
import { RevealStagger } from './Reveal';
import { getPackageTheme } from '../utils/packageTheme';

const PACKAGE_INFO = {
    economy: { name: 'Economy', lessons: 25 },
    business: { name: 'Business', lessons: 35 },
    first_class: { name: 'First Class', lessons: 45 },
    spoedopleiding_5: { name: 'Spoed 5u', lessons: 5 },
    spoedopleiding_10: { name: 'Spoed 10u', lessons: 10 },
    spoedopleiding_16_5: { name: 'Spoed 16,5u', lessons: 16.5 },
    spoedopleiding_25: { name: 'Spoed 25u', lessons: 25 },
    spoedopleiding: { name: 'Spoedopleiding', lessons: 0 },
    nationale_rijschool: { name: 'Nationale Rijschool', lessons: 0, external: true },
};

// Total CBR skill count — matches DRIVING_SKILLS on the backend. Used as
// denominator so the list % matches the leskaart % (un-scored skills weigh
// in as 0 in both places).
const TOTAL_SKILL_COUNT = 26;

// Overall progress = average score (1-5) across all skills, expressed as %.
// This gives new/partial students a meaningful, moving percentage instead of
// being stuck at 0% until every skill hits 4+.
function getOverallProgress(skillScores) {
    if (!skillScores) return 0;
    const scores = Object.values(skillScores).map(Number).filter(n => !isNaN(n));
    if (scores.length === 0) return 0;
    const sum = scores.reduce((a, b) => a + b, 0);
    const denominator = Math.max(scores.length, TOTAL_SKILL_COUNT);
    return Math.round((sum / (denominator * 5)) * 100);
}

function getProgressColor(progress) {
    if (progress >= 70) return 'bg-emerald-500';
    if (progress >= 40) return 'bg-amber-500';
    return 'bg-gray-500';
}

export default function StudentList({ students, onSelect, isInstructor, onDelete, onArchive, lessonCounts, plannedCounts }) {
    const sortedStudents = [...(students || [])].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'nl', { sensitivity: 'base' })
    );
    return (
        <RevealStagger className="divide-y divide-[#4A5568]">
            {sortedStudents.map((student) => {
                const isExternal = !!student.external || student.package_type === 'nationale_rijschool';
                const progress = getOverallProgress(student.skill_scores);
                const pkg = PACKAGE_INFO[student.package_type];
                const theme = getPackageTheme(student.package_type);
                const initialLessons = parseFloat(student.initial_lessons) || 0;
                const extraLessons = student.extra_lessons || 0;
                const freeLessons = student.free_lessons || 0;
                const pkgAfterInitial = pkg ? Math.max(0, pkg.lessons - initialLessons) : 0;
                const boughtLessons = pkgAfterInitial + extraLessons + freeLessons;
                const completedLessons = lessonCounts?.[student.id] || 0;
                const plannedLessons = plannedCounts?.[student.id] || 0;
                // Denominator auto-expands so 8 driven of 7 bought → 8/8.
                const totalLessons = Math.max(boughtLessons, completedLessons + plannedLessons);
                const isArchived = student.status === 'archived';

                // External students have no leskaart — clicking does nothing meaningful for them
                const handleOpen = () => {
                    if (isExternal) return;
                    onSelect(student);
                };

                return (
                    <div
                        key={student.id}
                        className={`p-4 ${isExternal ? '' : 'hover:bg-[#3D4A5C] cursor-pointer'} transition-colors flex items-center justify-between group relative ${isArchived ? 'opacity-60' : ''}`}
                        style={{
                            boxShadow: isArchived ? 'none' : `inset 4px 0 0 ${theme.primary}, 0 0 22px -8px ${theme.auraSoft}`,
                        }}
                        data-testid={`student-item-${student.id}`}
                    >
                        <div className="flex items-center gap-4 flex-1" onClick={handleOpen}>
                            <div
                                className="w-12 h-12 rounded-full flex items-center justify-center border"
                                style={{
                                    backgroundColor: isExternal ? `${theme.primary}1A` : '#1A202C',
                                    borderColor: isExternal ? `${theme.primary}66` : '#4A5568',
                                }}
                            >
                                {isExternal
                                    ? <Buildings size={22} weight="fill" style={{ color: theme.primary }} />
                                    : <User size={24} className="text-[#D4A537]" />}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-medium text-white">{student.name}</h3>
                                    {isExternal && (
                                        <span
                                            className="px-2 py-0.5 text-[10px] uppercase tracking-widest rounded font-bold"
                                            style={{
                                                backgroundColor: `${theme.primary}26`,
                                                color: theme.light,
                                                border: `1px solid ${theme.primary}66`,
                                            }}
                                        >
                                            Extern
                                        </span>
                                    )}
                                    {isArchived && (
                                        <span className="px-2 py-0.5 text-xs bg-gray-600 text-gray-300 rounded">Archief</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-sm text-gray-400 flex-wrap">
                                    {pkg && (
                                        <span className={`flex items-center gap-1 px-2 py-0.5 border rounded text-xs font-medium ${theme.pillClass}`}>
                                            {isExternal
                                                ? <Buildings size={12} weight="fill" />
                                                : <AirplaneTilt size={12} weight="fill" />}
                                            {pkg.name}
                                        </span>
                                    )}
                                    {!isExternal && totalLessons > 0 && (
                                        <span className="text-xs">
                                            <span className="text-white font-medium">{completedLessons}</span>
                                            <span className="text-gray-500">/{totalLessons} lessen</span>
                                            {extraLessons > 0 && <span className="text-[#D4A537] ml-1">(+{extraLessons})</span>}
                                            {freeLessons > 0 && <span className="text-emerald-400 ml-1">(+{freeLessons} gratis)</span>}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        {student.start_date || 'Onbekend'}
                                    </span>
                                    {student.phone && (
                                        <span className="flex items-center gap-1">
                                            <Phone size={12} />
                                            {student.phone}
                                        </span>
                                    )}
                                    {!isExternal && student.exam_date && (
                                        <span className="text-xs text-amber-400">
                                            Examen: {student.exam_date}
                                        </span>
                                    )}
                                    {!isExternal && <LevelBadge progress={progress} size="small" />}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Lesson counter bar — hidden for external students */}
                            {!isExternal && totalLessons > 0 && (
                                <div className="hidden md:block w-20 mr-2">
                                    <AnimatedProgressBar
                                        pct={Math.min(100, (completedLessons / totalLessons) * 100)}
                                        barClassName={theme.solidBarClass}
                                        tracksClassName="bg-[#1A202C]"
                                        height={8}
                                        duration={1300}
                                    />
                                </div>
                            )}
                            {/* Progress — hidden for external students */}
                            {!isExternal && (
                                <div className="text-right" onClick={handleOpen}>
                                    <div className="text-sm text-gray-400 mb-1">Voortgang</div>
                                    <div className="flex items-center gap-2 w-32">
                                        <AnimatedProgressBar
                                            pct={progress}
                                            barClassName={theme.solidBarClass}
                                            tracksClassName="bg-[#1A202C]"
                                            height={8}
                                            duration={1400}
                                            showPercent
                                            labelClassName="text-sm font-medium text-white tabular-nums w-10 text-right"
                                        />
                                    </div>
                                </div>
                            )}
                            {/* Actions */}
                            {isInstructor && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); onArchive?.(student.id); }}
                                        className="text-gray-500 hover:text-amber-400 hover:bg-amber-900/20 h-8 w-8 p-0"
                                        title={isArchived ? 'Herstellen' : 'Archiveren'}
                                        data-testid={`archive-student-${student.id}`}
                                    >
                                        <Archive size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); onDelete?.(student.id); }}
                                        className="text-gray-500 hover:text-red-400 hover:bg-red-900/20 h-8 w-8 p-0"
                                        title="Verwijderen"
                                        data-testid={`delete-student-${student.id}`}
                                    >
                                        <Trash size={16} />
                                    </Button>
                                </div>
                            )}
                            {!isExternal && (
                                <CaretRight
                                    size={20}
                                    className="text-gray-500 group-hover:text-[#D4A537] transition-colors"
                                    onClick={handleOpen}
                                />
                            )}
                        </div>
                    </div>
                );
            })}
        </RevealStagger>
    );
}
